import { createContext, useContext, useEffect, useState } from 'react'
import RoutesManagment from '../Routes/RoutesManagment';
import { logIn, ResetPassword, UsersForChangePassword, ChangePassword, LogOut } from '../Services/AuthenticationService';
import { CartProvider } from './CartProvider';
import { CompanyInfoProvider } from './CompanyInfoProvider';
import { FavoriteItemsProvider } from './FavoriteProductsProvider';
import LoadingSpinner from '../Components/Components/LoadingSpinner';
import useHandleAuthState from '../Hooks/useHandleAuthState';
import { CustomerProvider } from './CustomerProvider';
import { useConfig } from './ConfigProvider';
import { LastWatchedProductsProvider } from './LastWatchedProductsProvider';
import { InvoicesProvider } from './InvoicesProvider';
import { UpdateUserMarketing } from '../Services/UserService';
import { OffersProvider } from './OffersProvider';
import { SavedBasketsProvider } from './SavedBasketsProvider';
import { UserProvider } from './UserProvider';
import { useParams } from './ParamsProvider';
import { toast } from 'react-toastify';
import { GetCurrentUser, GetUserPassword, UpdateAcceptedAgreementsVersion } from '../Services/AccountService';
import { DiscountProvider } from './DiscountProvider';
import { setAccessToken, getAccessToken } from '../Services/TokenService';
import { useNavigate, useLocation } from 'react-router-dom';
import CrmProvider from './CrmProvider';
import PaymentProvider from './PaymentProvider';

const AuthContext = createContext()

export const useAuth = () => {
    return useContext(AuthContext)
}

const noAutoLogoutPaths = ["/confirmaccount", "/accountcreated", "/changepassword", "/login"]

const AuthProvider = ({ children }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { api, logOutTrigger } = useConfig()
    const { authState, pending, setAuthState, setRefresh } = useHandleAuthState()
    const { autoLogOutTime } = useParams()
    const [wholesalePrices, setWholesalePrices] = useState(!!authState.user)
    const [loginError, setLoginError] = useState("")

    const isAutoLogoutBlocked = (() => {
        const path = location.pathname.toLowerCase()
        const params = new URLSearchParams(location.search.toLowerCase())
        const isCartWithCustId = path === "/cart" && params.has("custid")
        const isOnPaymentSummary = path === "/" && (
            params.has("hash") || (params.has("sessionid") && params.has("paymentlink"))
        )
        return noAutoLogoutPaths.includes(path) || isCartWithCustId || isOnPaymentSummary
    })()

    useEffect(() => {
        setWholesalePrices(!!authState.user)
    }, [authState.user])

    useEffect(() => { //trigger na wylogowanie gdy wygaśnie token
        if (logOutTrigger !== null && authState.user) {
            logout(false, true)
        }
    }, [logOutTrigger])

    useEffect(() => { //timer na automatycznne odświeżenie tokena           
        if (!authState.user || !api) {
            return
        }
        const interval = setInterval(async () => {
            try {
                if (!getAccessToken()) {
                    clearInterval(interval)
                    return
                }

                const response = await api.post("/api/v1/Authentication/refreshToken", {}, { withCredentials: true })

                if (response.data) {
                    setAccessToken(response.data.accessToken)
                }
            } catch (err) {
                var accountIsNoLongerActive = true
                if (err.response.status != 401) {
                    console.error("Błąd przy odświeżaniu tokena:", err)
                    accountIsNoLongerActive = false
                }
                logout(true, true, accountIsNoLongerActive)
            }
        }, autoLogOutTime * 60 * 1000)

        return () => clearInterval(interval)
    }, [authState.user])

    const login = async (data, goToHomePage = true) => {
        setLoginError("")
        try {
            const res = await logIn(api, data)
            if (res.data.errorMessage && res.data.errorMessage.trim() !== '') {
                return { manyCompanies: false, companies: null, errorMessage: res.data.errorMessage }
            }
            if (res.data.companies && res.data.companies.length > 0) {
                return { manyCompanies: true, companies: res.data.companies, errorMessage: res.data.errorMessage }
            }
            else {
                clearLocalStorageData()
                setAccessToken(res.data.accessToken)
                const userRes = await GetCurrentUser(api)
                const newState = {
                    user: userRes.data,
                    custId: userRes.data.custId,
                    siteId: userRes.data.siteId,
                    whId: userRes.data.whId,
                    currency: userRes.data.currency,
                    selectedCustomer: null,
                    selectedCustomerTax: null,
                    selectedCustomerData: null,
                    initialSiteId: userRes.data.siteId,
                    initialWhId: userRes.data.whId,
                }
                setAuthState(newState)

                if (goToHomePage) {
                    navigate('/', { replace: true })
                }
                else {
                    setRefresh(true)
                }
                return { manyCompanies: false, companies: null, errorMessage: res.data.errorMessage }
            }
        } catch (e) {
            console.error(e)
            if (e.response && e.response.status === 401) {
                setLoginError(e.response.data)
            } else {
                setLoginError("Błąd połączenia z serwerem")
            }
        }
    }

    const updateConsentForUser = (consentMarketing, email, sms, phone) => {
        UpdateUserMarketing(api, consentMarketing, email, sms, phone)
            .then(() => {
                setRefresh(true)
            })
            .catch(e => {
                console.error(e)
            })
    }

    const logout = async (goToLoginPage = true, tokenExpired = false) => {
        try {
            await LogOut(api)
        } catch (error) {
            console.log(error)
        }
        logOutClearDataFuncBody()

        if (tokenExpired) {
            if (!isAutoLogoutBlocked) {
                navigate("/login?autoLogout")
            }
            return
        }
        if (goToLoginPage) {
            navigate("/login")
        }
    }

    const logOutClearDataFuncBody = () => {
        clearLocalStorageData()
        setAuthState({
            user: null,
            custId: null,
            siteId: null,
            whId: null,
            currency: 'PLN',
            selectedCustomer: null,
            selectedCustomerTax: null,
            selectedCustomerData: null
        })
        if ('setAppBadge' in navigator) { //usuwanie kropki powiadomien
            navigator.clearAppBadge().catch(e => console.error(e))
        }
    }

    const clearLocalStorageData = () => {
        localStorage.removeItem('APP_LICENSE')
        localStorage.removeItem('authState')
        localStorage.removeItem('categoryNavigationTree')
        localStorage.removeItem('indexMask')
        localStorage.removeItem('priceListExportMaxArticle')
        localStorage.removeItem('productDisplayMode')
        localStorage.removeItem('siteId')
        localStorage.removeItem('whId')
        localStorage.removeItem('NumberOfUnreadCustomerOffer')
        localStorage.removeItem('maintenance_bypass')
        sessionStorage.clear()
        setAccessToken(null)
    }

    const changeSelectedCustomer = (custId, siteId, whId, selectedCustomer, fullName, currency, selectedCustomerTax, zipCode, city, street, houseNum, localNum, email, phone, dpl) => {
        setAuthState(prev => ({
            ...prev,
            custId,
            siteId,
            whId,
            currency: currency || prev.currency,
            selectedCustomer,
            selectedCustomerTax,
            selectedCustomerData: {
                name: selectedCustomer,
                fullName: fullName,
                zipCode,
                city,
                street,
                houseNum,
                localNum,
                email,
                phone,
                tax: selectedCustomerTax,
                whId,
                custId,
                siteId,
                dpl
            }
        }))
    }

    const handleSelectCustomer = (customer) => {
        setAuthState(prev => ({
            ...prev,
            selectedCustomerObject: customer
        }))
    }

    const changeSiteIdAndWhId = (siteId, whId) => {
        setAuthState(prev => ({
            ...prev,
            siteId,
            whId
        }))
    }
    const handleResetPassword = async (email) => {
        try {
            const res = await ResetPassword(api, email)
            return res.data
        } catch (error) {
            console.log(error)
            return { resetType: 0, message: 'Wewnętrzny błąd aplikacji!' }
        }
    }
    const handleCheckChangePasswordLink = async (link) => {
        try {
            const res = await UsersForChangePassword(api, link)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }
    const handleChangePasswordFromLink = async (link, custId, password, marketingAgree, marketingAgreeEmail, marketingAgreePhone, marketingAgreeSms, isNew, agreementsVersion) => {
        try {
            const res = await ChangePassword(api, link, custId, password, marketingAgree, marketingAgreeEmail, marketingAgreePhone, marketingAgreeSms, isNew, agreementsVersion)
            return { result: true, errorMessage: '' }
        } catch (error) {
            console.log(error)
            return { result: false, errorMessage: error.response.data }
        }
    }

    const handleAdmitEInvoiceAgreement = (invoiceEmail) => {
        setAuthState(prev => ({
            ...prev,
            user: {
                ...prev.user,
                customerInfo: prev.user.customerInfo.map((item, index) =>
                    index === 0 ? { ...item, isEInvoiceConsent: true, eInvoiceEmail: invoiceEmail } : item
                )
            }
        }))
    }

    const handleGetUserPassword = async () => {
        try {
            let res = await GetUserPassword(api)
            return res.data
        } catch (e) {
            console.log(e)
            toast.error('Błąd w trakcie pobierania hasła')
            return ''
        }
    }

    const handleUpdateAcceptedAgreementsVersion = async (userId, accepptedAgreementsVersion) => {
        try {
            let res = await UpdateAcceptedAgreementsVersion(api, userId, accepptedAgreementsVersion)
            if (res.data) {
                //Aktualizacja wersji w pobranym obiekcie usera
                setAuthState(prev => ({
                    ...prev,
                    user: {
                        ...prev.user,
                        acceptedAgreementsVersion: accepptedAgreementsVersion
                    }
                }))
            }
            return res.data
        } catch (error) {
            console.log(error)
            return false
        }
    }

    if (pending) {
        return (
            <div className='row vh-100 align-items-center' style={{ overflow: 'none', backgroundColor: 'white' }}>
                <LoadingSpinner />
            </div>
        )
    }

    return (
        <AuthContext.Provider value={{
            authState,
            loginError,
            wholesalePrices,
            setWholesalePrices,
            changeSelectedCustomer,
            changeSiteIdAndWhId,
            updateConsentForUser,
            handleResetPassword,
            handleCheckChangePasswordLink,
            handleChangePasswordFromLink,
            logOutClearDataFuncBody,
            handleAdmitEInvoiceAgreement,
            handleSelectCustomer,
            handleGetUserPassword,
            login,
            logout,
            setLoginError,
            handleUpdateAcceptedAgreementsVersion,
            isAutoLogoutBlocked,
        }}>
            <DiscountProvider>
                <PaymentProvider>
                    <CompanyInfoProvider>
                        <LastWatchedProductsProvider>
                            <SavedBasketsProvider>
                                <FavoriteItemsProvider>
                                    <OffersProvider>
                                        <CartProvider>
                                            <CustomerProvider>
                                                <UserProvider>
                                                    <InvoicesProvider>
                                                        <CrmProvider>
                                                            <RoutesManagment />
                                                        </CrmProvider>
                                                    </InvoicesProvider>
                                                </UserProvider>
                                            </CustomerProvider>
                                        </CartProvider>
                                    </OffersProvider>
                                </FavoriteItemsProvider>
                            </SavedBasketsProvider>
                        </LastWatchedProductsProvider>
                    </CompanyInfoProvider>
                </PaymentProvider>
            </DiscountProvider>
        </AuthContext.Provider>
    )
}

export default AuthProvider

